import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import type { WebsiteDraft, WebsiteStepProps } from '../types';
import { config } from '../../config';
import { publicURL as websitePublicURL } from '../../api/websites';

// PreviewPublish is step 4 of the wizard.
//
// On mount we call saveDraft() — that persists the in-memory draft to the
// server (branding via PATCH /general, team visibility per-team via
// PATCH /teams/{id}, template + modules via PUT /website) WITHOUT
// publishing. The server returns the website row with a fresh
// preview_token, which we use to iframe /c/<slug>?preview=<token>.
//
// The iframe always loads — first-time wizards no longer see a "publish
// to see the preview" placeholder. Existing-website edits get the same
// auto-save → iframe path so the preview reflects the in-progress wizard
// state, not the currently-published version.
//
// We save once on mount (not on every keystroke) — the assumption is
// that the user has finished tweaking by the time they reach this step.
// A small "Refresh preview" button below the iframe re-saves + reloads
// for the rare case where they backed up to an earlier step, changed
// something, and want to see it in the iframe without leaving step 4.

export function PreviewPublish({ draft, saveDraft }: WebsiteStepProps) {
  const { t } = useTranslation('common');
  const { slug = '' } = useParams<{ slug: string }>();
  const baseURL = useMemo(
    () => websitePublicURL(config.apiURL, config.publicSitesBaseDomain, slug),
    [slug],
  );

  const [previewURL, setPreviewURL] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'ready' | 'failed'>('saving');
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  // refresh runs saveDraft → resolves a fresh preview URL → forces the
  // iframe to reload by setting src (which the browser treats as a new
  // navigation even if the URL string is identical to before).
  async function refresh() {
    setSaveState('saving');
    const result = await saveDraft();
    if (!result) {
      setSaveState('failed');
      return;
    }
    const url = result.preview_token
      ? `${baseURL}?preview=${encodeURIComponent(result.preview_token)}`
      : baseURL; // server didn't issue a token; fall back to the published URL
    setPreviewURL(url);
    setSaveState('ready');
  }

  // Auto-save + load on mount. The empty dep array is intentional —
  // re-saving every time draft changes (which is on every keystroke
  // earlier in the wizard) would be too noisy. The Refresh button
  // covers the "I backed up + tweaked + want to see it again" case.
  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enabledList = Array.from(draft.enabledModules);
  const teamCount = Array.from(draft.teamSelection.values()).filter(Boolean).length;

  return (
    <div className="wizard-preview">
      <section className="wizard-preview__summary">
        <h3>{t('website.wizard.preview.summaryHeading')}</h3>
        <dl className="wizard-preview__summary-list">
          <div>
            <dt>{t('website.wizard.preview.template')}</dt>
            <dd>{t(`website.wizard.template.${draft.template}.name`)}</dd>
          </div>
          <div>
            <dt>{t('website.wizard.preview.modules')}</dt>
            <dd>
              {enabledList.length === 0
                ? t('website.wizard.preview.noModules')
                : enabledList.map((m) => t(`website.wizard.module.${m}.label`)).join(', ')}
            </dd>
          </div>
          <div>
            <dt>{t('website.wizard.preview.teams')}</dt>
            <dd>{teamCount}</dd>
          </div>
          <div>
            <dt>{t('website.wizard.preview.url')}</dt>
            <dd>
              <a href={baseURL} target="_blank" rel="noopener noreferrer">{baseURL}</a>
            </dd>
          </div>
        </dl>
        {draft.websiteId && (
          <p className="wizard-preview__design-link">
            <Link
              className="btn btn--ghost"
              to={`/orgs/${encodeURIComponent(slug)}/website/${encodeURIComponent(draft.websiteId)}/design`}
            >
              {t('website.wizard.preview.openDesignMode')}
            </Link>
          </p>
        )}
      </section>

      <section className="wizard-preview__iframe-wrap">
        <header className="wizard-preview__iframe-head">
          <h3>{t('website.wizard.preview.previewHeading')}</h3>
          <button
            type="button"
            className="btn btn--ghost wizard-preview__refresh"
            onClick={() => void refresh()}
            disabled={saveState === 'saving'}
          >
            {saveState === 'saving'
              ? t('common.submitting')
              : t('website.wizard.preview.refresh')}
          </button>
        </header>
        <p className="wizard-preview__hint">{t('website.wizard.preview.previewHint')}</p>
        {saveState === 'failed' ? (
          <div className="wizard-preview__iframe-placeholder">
            {t('website.wizard.preview.saveFailed')}
          </div>
        ) : previewURL ? (
          <iframe
            ref={iframeRef}
            key={previewURL /* force remount on URL change so reload is honored */}
            className="wizard-preview__iframe"
            src={previewURL}
            title={t('website.wizard.preview.iframeTitle')}
            loading="lazy"
          />
        ) : (
          <div className="wizard-preview__iframe-placeholder">
            {t('common.loading')}
          </div>
        )}
      </section>
    </div>
  );
}

export function previewPublishIsValid(_d: WebsiteDraft): boolean {
  return true;
}
